export type Country = {
    name: string;
    image: string;
    label: string;
    code: string; // ISO 3166-1 alpha-2
    dialCode: string; // e.g. "+1"
};

/**
 * Import all flag images at build time so Vite processes them correctly
 * This ensures they work in production builds (S3/CloudFront)
 * The glob returns paths like: '@/assets/images/flag/us.webp' -> { default: '/assets/us.abc123.webp' }
 */
const flagImages = import.meta.glob<{ default: string }>('@/assets/images/flag/*.webp', { eager: true });

/**
 * Create a map of country code to processed image URL
 * Extract the country code from the import path and map it to the processed URL
 */
const flagImageMap = new Map<string, string>();
for (const [path, module] of Object.entries(flagImages)) {
    // Extract country code from path like '@/assets/images/flag/us.webp'
    const match = path.match(/flag\/([a-z]{2})\.webp$/);
    const countryCode = match?.[1];
    if (countryCode && module && 'default' in module && typeof module.default === 'string') {
        flagImageMap.set(countryCode, module.default);
    }
}

/**
 * Helper function to get the correct flag image path for production builds
 * Uses Vite's glob import to ensure all flags are processed at build time
 */
export function getFlagImagePath(code: string): string {
    const lowerCode = code.toLowerCase();
    const imageUrl = flagImageMap.get(lowerCode);

    if (imageUrl) {
        return imageUrl;
    }

    // Fallback: construct path manually if import not found
    // This should not happen if all flags are properly imported
    console.warn(`Flag image not found for code: ${code}`);
    return `/assets/images/flag/${lowerCode}.webp`;
}

// Trimmed list focused on common countries. Extend as needed.
export const countries: Country[] = [
    {
        name: 'Afghanistan',
        image: getFlagImagePath('af'),
        label: 'AF +93',
        code: 'AF',
        dialCode: '+93',
    },
    {
        name: 'Albania',
        image: getFlagImagePath('al'),
        label: 'AL +355',
        code: 'AL',
        dialCode: '+355',
    },
    {
        name: 'Algeria',
        image: getFlagImagePath('dz'),
        label: 'DZ +213',
        code: 'DZ',
        dialCode: '+213',
    },
    {
        name: 'American Samoa',
        image: getFlagImagePath('as'),
        label: 'AS +1684',
        code: 'AS',
        dialCode: '+1684',
    },
    {
        name: 'Andorra',
        image: getFlagImagePath('ad'),
        label: 'AD +376',
        code: 'AD',
        dialCode: '+376',
    },
    {
        name: 'Angola',
        image: getFlagImagePath('ao'),
        label: 'AO +244',
        code: 'AO',
        dialCode: '+244',
    },
    {
        name: 'Anguilla',
        image: getFlagImagePath('ai'),
        label: 'AI +1264',
        code: 'AI',
        dialCode: '+1264',
    },
    {
        name: 'Antarctica',
        image: getFlagImagePath('aq'),
        label: 'AQ +672',
        code: 'AQ',
        dialCode: '+672',
    },
    {
        name: 'Antigua & Barbuda',
        image: getFlagImagePath('ag'),
        label: 'AG +1268',
        code: 'AG',
        dialCode: '+1268',
    },
    {
        name: 'Argentina',
        image: getFlagImagePath('ar'),
        label: 'AR +54',
        code: 'AR',
        dialCode: '+54',
    },
    {
        name: 'Armenia',
        image: getFlagImagePath('am'),
        label: 'AM +374',
        code: 'AM',
        dialCode: '+374',
    },
    {
        name: 'Aruba',
        image: getFlagImagePath('aw'),
        label: 'AW +297',
        code: 'AW',
        dialCode: '+297',
    },
    {
        name: 'Australia',
        image: getFlagImagePath('au'),
        label: 'AU +61',
        code: 'AU',
        dialCode: '+61',
    },
    {
        name: 'Austria',
        image: getFlagImagePath('at'),
        label: 'AT +43',
        code: 'AT',
        dialCode: '+43',
    },
    {
        name: 'Azerbaijan',
        image: getFlagImagePath('az'),
        label: 'AZ +994',
        code: 'AZ',
        dialCode: '+994',
    },
    {
        name: 'Bahamas',
        image: getFlagImagePath('bs'),
        label: 'BS +1242',
        code: 'BS',
        dialCode: '+1242',
    },
    {
        name: 'Bahrain',
        image: getFlagImagePath('bh'),
        label: 'BH +973',
        code: 'BH',
        dialCode: '+973',
    },
    {
        name: 'Bangladesh',
        image: getFlagImagePath('bd'),
        label: 'BD +880',
        code: 'BD',
        dialCode: '+880',
    },
    {
        name: 'Barbados',
        image: getFlagImagePath('bb'),
        label: 'BB +1246',
        code: 'BB',
        dialCode: '+1246',
    },
    {
        name: 'Belarus',
        image: getFlagImagePath('by'),
        label: 'BY +375',
        code: 'BY',
        dialCode: '+375',
    },
    {
        name: 'Belgium',
        image: getFlagImagePath('be'),
        label: 'BE +32',
        code: 'BE',
        dialCode: '+32',
    },
    {
        name: 'Belize',
        image: getFlagImagePath('bz'),
        label: 'BZ +501',
        code: 'BZ',
        dialCode: '+501',
    },
    {
        name: 'Benin',
        image: getFlagImagePath('bj'),
        label: 'BJ +229',
        code: 'BJ',
        dialCode: '+229',
    },
    {
        name: 'Bermuda',
        image: getFlagImagePath('bm'),
        label: 'BM +1441',
        code: 'BM',
        dialCode: '+1441',
    },
    {
        name: 'Bhutan',
        image: getFlagImagePath('bt'),
        label: 'BT +975',
        code: 'BT',
        dialCode: '+975',
    },
    {
        name: 'Bolivia',
        image: getFlagImagePath('bo'),
        label: 'BO +591',
        code: 'BO',
        dialCode: '+591',
    },
    {
        name: 'Bosnia and Herzegovina',
        image: getFlagImagePath('ba'),
        label: 'BA +387',
        code: 'BA',
        dialCode: '+387',
    },
    {
        name: 'Botswana',
        image: getFlagImagePath('bw'),
        label: 'BW +267',
        code: 'BW',
        dialCode: '+267',
    },
    {
        name: 'Brazil',
        image: getFlagImagePath('br'),
        label: 'BR +55',
        code: 'BR',
        dialCode: '+55',
    },
    {
        name: 'British Indian Ocean Territory',
        image: getFlagImagePath('io'),
        label: 'IO +246',
        code: 'IO',
        dialCode: '+246',
    },
    {
        name: 'Brunei Darussalam',
        image: getFlagImagePath('bn'),
        label: 'BN +673',
        code: 'BN',
        dialCode: '+673',
    },
    {
        name: 'Bulgaria',
        image: getFlagImagePath('bg'),
        label: 'BG +359',
        code: 'BG',
        dialCode: '+359',
    },
    {
        name: 'Burkina Faso',
        image: getFlagImagePath('bf'),
        label: 'BF +226',
        code: 'BF',
        dialCode: '+226',
    },
    {
        name: 'Burundi',
        image: getFlagImagePath('bi'),
        label: 'BI +257',
        code: 'BI',
        dialCode: '+257',
    },
    {
        name: 'Cambodia',
        image: getFlagImagePath('kh'),
        label: 'KH +855',
        code: 'KH',
        dialCode: '+855',
    },
    {
        name: 'Cameroon',
        image: getFlagImagePath('cm'),
        label: 'CM +237',
        code: 'CM',
        dialCode: '+237',
    },
    {
        name: 'Canada',
        image: getFlagImagePath('ca'),
        label: 'CA +1',
        code: 'CA',
        dialCode: '+1',
    },
    {
        name: 'Cape Verde',
        image: getFlagImagePath('cv'),
        label: 'CV +238',
        code: 'CV',
        dialCode: '+238',
    },
    {
        name: 'Cayman Islands',
        image: getFlagImagePath('ky'),
        label: 'KY +1345',
        code: 'KY',
        dialCode: '+1345',
    },
    {
        name: 'Central African Republic',
        image: getFlagImagePath('cf'),
        label: 'CF +236',
        code: 'CF',
        dialCode: '+236',
    },
    {
        name: 'Chad',
        image: getFlagImagePath('td'),
        label: 'TD +235',
        code: 'TD',
        dialCode: '+235',
    },
    {
        name: 'Chile',
        image: getFlagImagePath('cl'),
        label: 'CL +56',
        code: 'CL',
        dialCode: '+56',
    },
    {
        name: 'China',
        image: getFlagImagePath('cn'),
        label: 'CN +86',
        code: 'CN',
        dialCode: '+86',
    },
    {
        name: 'Christmas Island',
        image: getFlagImagePath('cx'),
        label: 'CX +61',
        code: 'CX',
        dialCode: '+61',
    },
    {
        name: 'Cocos (Keeling) Islands',
        image: getFlagImagePath('cc'),
        label: 'CC +61',
        code: 'CC',
        dialCode: '+61',
    },
    {
        name: 'Colombia',
        image: getFlagImagePath('co'),
        label: 'CO +57',
        code: 'CO',
        dialCode: '+57',
    },
    {
        name: 'Comoros',
        image: getFlagImagePath('km'),
        label: 'KM +269',
        code: 'KM',
        dialCode: '+269',
    },
    {
        name: 'Congo - Brazzaville',
        image: getFlagImagePath('cg'),
        label: 'CG +242',
        code: 'CG',
        dialCode: '+242',
    },
    {
        name: 'Congo - Kinshasa',
        image: getFlagImagePath('cd'),
        label: 'CD +243',
        code: 'CD',
        dialCode: '+243',
    },
    {
        name: 'Cook Islands',
        image: getFlagImagePath('ck'),
        label: 'CK +682',
        code: 'CK',
        dialCode: '+682',
    },
    {
        name: 'Costa Rica',
        image: getFlagImagePath('cr'),
        label: 'CR +506',
        code: 'CR',
        dialCode: '+506',
    },
    {
        name: 'Croatia',
        image: getFlagImagePath('hr'),
        label: 'HR +385',
        code: 'HR',
        dialCode: '+385',
    },
    {
        name: 'Cuba',
        image: getFlagImagePath('cu'),
        label: 'CU +53',
        code: 'CU',
        dialCode: '+53',
    },
    {
        name: 'Curaçao',
        image: getFlagImagePath('cw'),
        label: 'CW +599',
        code: 'CW',
        dialCode: '+599',
    },
    {
        name: 'Cyprus',
        image: getFlagImagePath('cy'),
        label: 'CY +357',
        code: 'CY',
        dialCode: '+357',
    },
    {
        name: 'Czech Republic',
        image: getFlagImagePath('cz'),
        label: 'CZ +420',
        code: 'CZ',
        dialCode: '+420',
    },
    {
        name: 'Côte dIvoire / Ivory Coast',
        image: getFlagImagePath('ci'),
        label: 'CI +225',
        code: 'CI',
        dialCode: '+225',
    },
    {
        name: 'Denmark',
        image: getFlagImagePath('dk'),
        label: 'DK +45',
        code: 'DK',
        dialCode: '+45',
    },
    {
        name: 'Djibouti',
        image: getFlagImagePath('dj'),
        label: 'DJ +253',
        code: 'DJ',
        dialCode: '+253',
    },
    {
        name: 'Dominica',
        image: getFlagImagePath('dm'),
        label: 'DM +1767',
        code: 'DM',
        dialCode: '+1767',
    },
    {
        name: 'Dominican Republic',
        image: getFlagImagePath('do'),
        label: 'DO +1',
        code: 'DO',
        dialCode: '+1',
    },
    {
        name: 'Ecuador',
        image: getFlagImagePath('ec'),
        label: 'EC +593',
        code: 'EC',
        dialCode: '+593',
    },
    {
        name: 'Egypt',
        image: getFlagImagePath('eg'),
        label: 'EG +20',
        code: 'EG',
        dialCode: '+20',
    },
    {
        name: 'El Salvador',
        image: getFlagImagePath('sv'),
        label: 'SV +503',
        code: 'SV',
        dialCode: '+503',
    },
    {
        name: 'Equatorial Guinea',
        image: getFlagImagePath('gq'),
        label: 'GQ +240',
        code: 'GQ',
        dialCode: '+240',
    },
    {
        name: 'Eritrea',
        image: getFlagImagePath('er'),
        label: 'ER +291',
        code: 'ER',
        dialCode: '+291',
    },
    {
        name: 'Estonia',
        image: getFlagImagePath('ee'),
        label: 'EE +372',
        code: 'EE',
        dialCode: '+372',
    },
    {
        name: 'Eswatini',
        image: getFlagImagePath('sz'),
        label: 'SZ +268',
        code: 'SZ',
        dialCode: '+268',
    },
    {
        name: 'Ethiopia',
        image: getFlagImagePath('et'),
        label: 'ET +251',
        code: 'ET',
        dialCode: '+251',
    },
    {
        name: 'Falkland Islands (Malvinas)',
        image: getFlagImagePath('fk'),
        label: 'FK +500',
        code: 'FK',
        dialCode: '+500',
    },
    {
        name: 'Faroe Islands',
        image: getFlagImagePath('fo'),
        label: 'FO +298',
        code: 'FO',
        dialCode: '+298',
    },
    {
        name: 'Fiji',
        image: getFlagImagePath('fj'),
        label: 'FJ +679',
        code: 'FJ',
        dialCode: '+679',
    },
    {
        name: 'Finland',
        image: getFlagImagePath('fi'),
        label: 'FI +358',
        code: 'FI',
        dialCode: '+358',
    },
    {
        name: 'France',
        image: getFlagImagePath('fr'),
        label: 'FR +33',
        code: 'FR',
        dialCode: '+33',
    },
    {
        name: 'French Guiana',
        image: getFlagImagePath('gf'),
        label: 'GF +594',
        code: 'GF',
        dialCode: '+594',
    },
    {
        name: 'French Polynesia',
        image: getFlagImagePath('pf'),
        label: 'PF +689',
        code: 'PF',
        dialCode: '+689',
    },
    {
        name: 'Gabon',
        image: getFlagImagePath('ga'),
        label: 'GA +241',
        code: 'GA',
        dialCode: '+241',
    },
    {
        name: 'Gambia',
        image: getFlagImagePath('gm'),
        label: 'GM +220',
        code: 'GM',
        dialCode: '+220',
    },
    {
        name: 'Georgia',
        image: getFlagImagePath('ge'),
        label: 'GE +995',
        code: 'GE',
        dialCode: '+995',
    },
    {
        name: 'Germany',
        image: getFlagImagePath('de'),
        label: 'DE +49',
        code: 'DE',
        dialCode: '+49',
    },
    {
        name: 'Ghana',
        image: getFlagImagePath('gh'),
        label: 'GH +233',
        code: 'GH',
        dialCode: '+233',
    },
    {
        name: 'Gibraltar',
        image: getFlagImagePath('gi'),
        label: 'GI +350',
        code: 'GI',
        dialCode: '+350',
    },
    {
        name: 'Greece',
        image: getFlagImagePath('gr'),
        label: 'GR +30',
        code: 'GR',
        dialCode: '+30',
    },
    {
        name: 'Greenland',
        image: getFlagImagePath('gl'),
        label: 'GL +299',
        code: 'GL',
        dialCode: '+299',
    },
    {
        name: 'Grenada',
        image: getFlagImagePath('gd'),
        label: 'GD +1473',
        code: 'GD',
        dialCode: '+1473',
    },
    {
        name: 'Guadeloupe',
        image: getFlagImagePath('gp'),
        label: 'GP +590',
        code: 'GP',
        dialCode: '+590',
    },
    {
        name: 'Guam',
        image: getFlagImagePath('gu'),
        label: 'GU +1671',
        code: 'GU',
        dialCode: '+1671',
    },
    {
        name: 'Guatemala',
        image: getFlagImagePath('gt'),
        label: 'GT +502',
        code: 'GT',
        dialCode: '+502',
    },
    {
        name: 'Guinea',
        image: getFlagImagePath('gn'),
        label: 'GN +224',
        code: 'GN',
        dialCode: '+224',
    },
    {
        name: 'Guinea-Bissau',
        image: getFlagImagePath('gw'),
        label: 'GW +245',
        code: 'GW',
        dialCode: '+245',
    },
    {
        name: 'Guyana',
        image: getFlagImagePath('gy'),
        label: 'GY +592',
        code: 'GY',
        dialCode: '+592',
    },
    {
        name: 'Haiti',
        image: getFlagImagePath('ht'),
        label: 'HT +509',
        code: 'HT',
        dialCode: '+509',
    },
    {
        name: 'Holy See (Vatican City State)',
        image: getFlagImagePath('va'),
        label: 'VA +379',
        code: 'VA',
        dialCode: '+379',
    },
    {
        name: 'Honduras',
        image: getFlagImagePath('hn'),
        label: 'HN +504',
        code: 'HN',
        dialCode: '+504',
    },
    {
        name: 'Hong Kong',
        image: getFlagImagePath('hk'),
        label: 'HK +852',
        code: 'HK',
        dialCode: '+852',
    },
    {
        name: 'Hungary',
        image: getFlagImagePath('hu'),
        label: 'HU +36',
        code: 'HU',
        dialCode: '+36',
    },
    {
        name: 'Iceland',
        image: getFlagImagePath('is'),
        label: 'IS +354',
        code: 'IS',
        dialCode: '+354',
    },
    {
        name: 'India',
        image: getFlagImagePath('in'),
        label: 'IN +91',
        code: 'IN',
        dialCode: '+91',
    },
    {
        name: 'Indonesia',
        image: getFlagImagePath('id'),
        label: 'ID +62',
        code: 'ID',
        dialCode: '+62',
    },
    {
        name: 'Iran',
        image: getFlagImagePath('ir'),
        label: 'IR +98',
        code: 'IR',
        dialCode: '+98',
    },
    {
        name: 'Iraq',
        image: getFlagImagePath('iq'),
        label: 'IQ +964',
        code: 'IQ',
        dialCode: '+964',
    },
    {
        name: 'Ireland',
        image: getFlagImagePath('ie'),
        label: 'IE +353',
        code: 'IE',
        dialCode: '+353',
    },
    {
        name: 'Israel',
        image: getFlagImagePath('il'),
        label: 'IL +972',
        code: 'IL',
        dialCode: '+972',
    },
    {
        name: 'Italy',
        image: getFlagImagePath('it'),
        label: 'IT +39',
        code: 'IT',
        dialCode: '+39',
    },
    {
        name: 'Jamaica',
        image: getFlagImagePath('jm'),
        label: 'JM +1876',
        code: 'JM',
        dialCode: '+1876',
    },
    {
        name: 'Japan',
        image: getFlagImagePath('jp'),
        label: 'JP +81',
        code: 'JP',
        dialCode: '+81',
    },
    {
        name: 'Jordan',
        image: getFlagImagePath('jo'),
        label: 'JO +962',
        code: 'JO',
        dialCode: '+962',
    },
    {
        name: 'Kazakhstan',
        image: getFlagImagePath('kz'),
        label: 'KZ +77',
        code: 'KZ',
        dialCode: '+77',
    },
    {
        name: 'Kenya',
        image: getFlagImagePath('ke'),
        label: 'KE +254',
        code: 'KE',
        dialCode: '+254',
    },
    {
        name: 'Kiribati',
        image: getFlagImagePath('ki'),
        label: 'KI +686',
        code: 'KI',
        dialCode: '+686',
    },
    {
        name: 'Kuwait',
        image: getFlagImagePath('kw'),
        label: 'KW +965',
        code: 'KW',
        dialCode: '+965',
    },
    {
        name: 'Kyrgyzstan',
        image: getFlagImagePath('kg'),
        label: 'KG +996',
        code: 'KG',
        dialCode: '+996',
    },
    {
        name: 'Laos',
        image: getFlagImagePath('la'),
        label: 'LA +856',
        code: 'LA',
        dialCode: '+856',
    },
    {
        name: 'Latvia',
        image: getFlagImagePath('lv'),
        label: 'LV +371',
        code: 'LV',
        dialCode: '+371',
    },
    {
        name: 'Lebanon',
        image: getFlagImagePath('lb'),
        label: 'LB +961',
        code: 'LB',
        dialCode: '+961',
    },
    {
        name: 'Lesotho',
        image: getFlagImagePath('ls'),
        label: 'LS +266',
        code: 'LS',
        dialCode: '+266',
    },
    {
        name: 'Liberia',
        image: getFlagImagePath('lr'),
        label: 'LR +231',
        code: 'LR',
        dialCode: '+231',
    },
    {
        name: 'Libya',
        image: getFlagImagePath('ly'),
        label: 'LY +218',
        code: 'LY',
        dialCode: '+218',
    },
    {
        name: 'Liechtenstein',
        image: getFlagImagePath('li'),
        label: 'LI +423',
        code: 'LI',
        dialCode: '+423',
    },
    {
        name: 'Lithuania',
        image: getFlagImagePath('lt'),
        label: 'LT +370',
        code: 'LT',
        dialCode: '+370',
    },
    {
        name: 'Luxembourg',
        image: getFlagImagePath('lu'),
        label: 'LU +352',
        code: 'LU',
        dialCode: '+352',
    },
    {
        name: 'Macau',
        image: getFlagImagePath('mo'),
        label: 'MO +853',
        code: 'MO',
        dialCode: '+853',
    },
    {
        name: 'Madagascar',
        image: getFlagImagePath('mg'),
        label: 'MG +261',
        code: 'MG',
        dialCode: '+261',
    },
    {
        name: 'Malawi',
        image: getFlagImagePath('mw'),
        label: 'MW +265',
        code: 'MW',
        dialCode: '+265',
    },
    {
        name: 'Malaysia',
        image: getFlagImagePath('my'),
        label: 'MY +60',
        code: 'MY',
        dialCode: '+60',
    },
    {
        name: 'Maldives',
        image: getFlagImagePath('mv'),
        label: 'MV +960',
        code: 'MV',
        dialCode: '+960',
    },
    {
        name: 'Mali',
        image: getFlagImagePath('ml'),
        label: 'ML +223',
        code: 'ML',
        dialCode: '+223',
    },
    {
        name: 'Malta',
        image: getFlagImagePath('mt'),
        label: 'MT +356',
        code: 'MT',
        dialCode: '+356',
    },
    {
        name: 'Marshall Islands',
        image: getFlagImagePath('mh'),
        label: 'MH +692',
        code: 'MH',
        dialCode: '+692',
    },
    {
        name: 'Martinique',
        image: getFlagImagePath('mq'),
        label: 'MQ +596',
        code: 'MQ',
        dialCode: '+596',
    },
    {
        name: 'Mauritania',
        image: getFlagImagePath('mr'),
        label: 'MR +222',
        code: 'MR',
        dialCode: '+222',
    },
    {
        name: 'Mauritius',
        image: getFlagImagePath('mu'),
        label: 'MU +230',
        code: 'MU',
        dialCode: '+230',
    },
    {
        name: 'Mexico',
        image: getFlagImagePath('mx'),
        label: 'MX +52',
        code: 'MX',
        dialCode: '+52',
    },
    {
        name: 'Micronesia',
        image: getFlagImagePath('fm'),
        label: 'FM +691',
        code: 'FM',
        dialCode: '+691',
    },
    {
        name: 'Moldova',
        image: getFlagImagePath('md'),
        label: 'MD +373',
        code: 'MD',
        dialCode: '+373',
    },
    {
        name: 'Monaco',
        image: getFlagImagePath('mc'),
        label: 'MC +377',
        code: 'MC',
        dialCode: '+377',
    },
    {
        name: 'Mongolia',
        image: getFlagImagePath('mn'),
        label: 'MN +976',
        code: 'MN',
        dialCode: '+976',
    },
    {
        name: 'Montenegro',
        image: getFlagImagePath('me'),
        label: 'ME +382',
        code: 'ME',
        dialCode: '+382',
    },
    {
        name: 'Montserrat',
        image: getFlagImagePath('ms'),
        label: 'MS +1664',
        code: 'MS',
        dialCode: '+1664',
    },
    {
        name: 'Morocco',
        image: getFlagImagePath('ma'),
        label: 'MA +212',
        code: 'MA',
        dialCode: '+212',
    },
    {
        name: 'Mozambique',
        image: getFlagImagePath('mz'),
        label: 'MZ +258',
        code: 'MZ',
        dialCode: '+258',
    },
    {
        name: 'Myanmar',
        image: getFlagImagePath('mm'),
        label: 'MM +95',
        code: 'MM',
        dialCode: '+95',
    },
    {
        name: 'Namibia',
        image: getFlagImagePath('na'),
        label: 'NA +264',
        code: 'NA',
        dialCode: '+264',
    },
    {
        name: 'Nauru',
        image: getFlagImagePath('nr'),
        label: 'NR +674',
        code: 'NR',
        dialCode: '+674',
    },
    {
        name: 'Nepal',
        image: getFlagImagePath('np'),
        label: 'NP +977',
        code: 'NP',
        dialCode: '+977',
    },
    {
        name: 'Netherlands',
        image: getFlagImagePath('nl'),
        label: 'NL +31',
        code: 'NL',
        dialCode: '+31',
    },
    {
        name: 'New Caledonia',
        image: getFlagImagePath('nc'),
        label: 'NC +687',
        code: 'NC',
        dialCode: '+687',
    },
    {
        name: 'New Zealand',
        image: getFlagImagePath('nz'),
        label: 'NZ +64',
        code: 'NZ',
        dialCode: '+64',
    },
    {
        name: 'Nicaragua',
        image: getFlagImagePath('ni'),
        label: 'NI +505',
        code: 'NI',
        dialCode: '+505',
    },
    {
        name: 'Niger',
        image: getFlagImagePath('ne'),
        label: 'NE +227',
        code: 'NE',
        dialCode: '+227',
    },
    {
        name: 'Nigeria',
        image: getFlagImagePath('ng'),
        label: 'NG +234',
        code: 'NG',
        dialCode: '+234',
    },
    {
        name: 'Niue',
        image: getFlagImagePath('nu'),
        label: 'NU +683',
        code: 'NU',
        dialCode: '+683',
    },
    {
        name: 'Norfolk Island',
        image: getFlagImagePath('nf'),
        label: 'NF +672',
        code: 'NF',
        dialCode: '+672',
    },
    {
        name: 'North Korea',
        image: getFlagImagePath('kp'),
        label: 'KP +850',
        code: 'KP',
        dialCode: '+850',
    },
    {
        name: 'North Macedonia',
        image: getFlagImagePath('mk'),
        label: 'MK +389',
        code: 'MK',
        dialCode: '+389',
    },
    {
        name: 'Northern Mariana Islands',
        image: getFlagImagePath('mp'),
        label: 'MP +1670',
        code: 'MP',
        dialCode: '+1670',
    },
    {
        name: 'Norway',
        image: getFlagImagePath('no'),
        label: 'NO +47',
        code: 'NO',
        dialCode: '+47',
    },
    {
        name: 'Oman',
        image: getFlagImagePath('om'),
        label: 'OM +968',
        code: 'OM',
        dialCode: '+968',
    },
    {
        name: 'Pakistan',
        image: getFlagImagePath('pk'),
        label: 'PK +92',
        code: 'PK',
        dialCode: '+92',
    },
    {
        name: 'Palau',
        image: getFlagImagePath('pw'),
        label: 'PW +680',
        code: 'PW',
        dialCode: '+680',
    },
    {
        name: 'Panama',
        image: getFlagImagePath('pa'),
        label: 'PA +507',
        code: 'PA',
        dialCode: '+507',
    },
    {
        name: 'Papua New Guinea',
        image: getFlagImagePath('pg'),
        label: 'PG +675',
        code: 'PG',
        dialCode: '+675',
    },
    {
        name: 'Paraguay',
        image: getFlagImagePath('py'),
        label: 'PY +595',
        code: 'PY',
        dialCode: '+595',
    },
    {
        name: 'Peru',
        image: getFlagImagePath('pe'),
        label: 'PE +51',
        code: 'PE',
        dialCode: '+51',
    },
    {
        name: 'Philippines',
        image: getFlagImagePath('ph'),
        label: 'PH +63',
        code: 'PH',
        dialCode: '+63',
    },
    {
        name: 'Pitcairn',
        image: getFlagImagePath('pn'),
        label: 'PN +872',
        code: 'PN',
        dialCode: '+872',
    },
    {
        name: 'Poland',
        image: getFlagImagePath('pl'),
        label: 'PL +48',
        code: 'PL',
        dialCode: '+48',
    },
    {
        name: 'Portugal',
        image: getFlagImagePath('pt'),
        label: 'PT +351',
        code: 'PT',
        dialCode: '+351',
    },
    {
        name: 'Puerto Rico',
        image: getFlagImagePath('pr'),
        label: 'PR +1',
        code: 'PR',
        dialCode: '+1',
    },
    {
        name: 'Qatar',
        image: getFlagImagePath('qa'),
        label: 'QA +974',
        code: 'QA',
        dialCode: '+974',
    },
    {
        name: 'Reunion',
        image: getFlagImagePath('re'),
        label: 'RE +262',
        code: 'RE',
        dialCode: '+262',
    },
    {
        name: 'Romania',
        image: getFlagImagePath('ro'),
        label: 'RO +40',
        code: 'RO',
        dialCode: '+40',
    },
    {
        name: 'Russia',
        image: getFlagImagePath('ru'),
        label: 'RU +7',
        code: 'RU',
        dialCode: '+7',
    },
    {
        name: 'Rwanda',
        image: getFlagImagePath('rw'),
        label: 'RW +250',
        code: 'RW',
        dialCode: '+250',
    },
    {
        name: 'Samoa',
        image: getFlagImagePath('ws'),
        label: 'WS +685',
        code: 'WS',
        dialCode: '+685',
    },
    {
        name: 'San Marino',
        image: getFlagImagePath('sm'),
        label: 'SM +378',
        code: 'SM',
        dialCode: '+378',
    },
    {
        name: 'Sao Tome and Principe',
        image: getFlagImagePath('st'),
        label: 'ST +239',
        code: 'ST',
        dialCode: '+239',
    },
    {
        name: 'Saudi Arabia',
        image: getFlagImagePath('sa'),
        label: 'SA +966',
        code: 'SA',
        dialCode: '+966',
    },
    {
        name: 'Senegal',
        image: getFlagImagePath('sn'),
        label: 'SN +221',
        code: 'SN',
        dialCode: '+221',
    },
    {
        name: 'Serbia',
        image: getFlagImagePath('rs'),
        label: 'RS +381',
        code: 'RS',
        dialCode: '+381',
    },
    {
        name: 'Seychelles',
        image: getFlagImagePath('sc'),
        label: 'SC +248',
        code: 'SC',
        dialCode: '+248',
    },
    {
        name: 'Sierra Leone',
        image: getFlagImagePath('sl'),
        label: 'SL +232',
        code: 'SL',
        dialCode: '+232',
    },
    {
        name: 'Singapore',
        image: getFlagImagePath('sg'),
        label: 'SG +65',
        code: 'SG',
        dialCode: '+65',
    },
    {
        name: 'Slovakia',
        image: getFlagImagePath('sk'),
        label: 'SK +421',
        code: 'SK',
        dialCode: '+421',
    },
    {
        name: 'Slovenia',
        image: getFlagImagePath('si'),
        label: 'SI +386',
        code: 'SI',
        dialCode: '+386',
    },
    {
        name: 'Solomon Islands',
        image: getFlagImagePath('sb'),
        label: 'SB +677',
        code: 'SB',
        dialCode: '+677',
    },
    {
        name: 'Somalia',
        image: getFlagImagePath('so'),
        label: 'SO +252',
        code: 'SO',
        dialCode: '+252',
    },
    {
        name: 'South Africa',
        image: getFlagImagePath('za'),
        label: 'ZA +27',
        code: 'ZA',
        dialCode: '+27',
    },
    {
        name: 'South Georgia',
        image: getFlagImagePath('gs'),
        label: 'GS +500',
        code: 'GS',
        dialCode: '+500',
    },
    {
        name: 'South Korea',
        image: getFlagImagePath('kr'),
        label: 'KR +82',
        code: 'KR',
        dialCode: '+82',
    },
    {
        name: 'South Sudan',
        image: getFlagImagePath('ss'),
        label: 'SS +211',
        code: 'SS',
        dialCode: '+211',
    },
    {
        name: 'Spain',
        image: getFlagImagePath('es'),
        label: 'ES +34',
        code: 'ES',
        dialCode: '+34',
    },
    {
        name: 'Sri Lanka',
        image: getFlagImagePath('lk'),
        label: 'LK +94',
        code: 'LK',
        dialCode: '+94',
    },
    {
        name: 'St. Helena',
        image: getFlagImagePath('sh'),
        label: 'SH +290',
        code: 'SH',
        dialCode: '+290',
    },
    {
        name: 'St. Kitts & Nevis',
        image: getFlagImagePath('kn'),
        label: 'KN +1869',
        code: 'KN',
        dialCode: '+1869',
    },
    {
        name: 'St. Lucia',
        image: getFlagImagePath('lc'),
        label: 'LC +1758',
        code: 'LC',
        dialCode: '+1758',
    },
    {
        name: 'St. Pierre & Miquelon',
        image: getFlagImagePath('pm'),
        label: 'PM +508',
        code: 'PM',
        dialCode: '+508',
    },
    {
        name: 'St. Vincent & Grenadines',
        image: getFlagImagePath('vc'),
        label: 'VC +1784',
        code: 'VC',
        dialCode: '+1784',
    },
    {
        name: 'Sudan',
        image: getFlagImagePath('sd'),
        label: 'SD +249',
        code: 'SD',
        dialCode: '+249',
    },
    {
        name: 'Suriname',
        image: getFlagImagePath('sr'),
        label: 'SR +597',
        code: 'SR',
        dialCode: '+597',
    },
    {
        name: 'Svalbard and Jan Mayen',
        image: getFlagImagePath('sj'),
        label: 'SJ +47',
        code: 'SJ',
        dialCode: '+47',
    },
    {
        name: 'Sweden',
        image: getFlagImagePath('se'),
        label: 'SE +46',
        code: 'SE',
        dialCode: '+46',
    },
    {
        name: 'Switzerland',
        image: getFlagImagePath('ch'),
        label: 'CH +41',
        code: 'CH',
        dialCode: '+41',
    },
    {
        name: 'Syrian Arab Republic',
        image: getFlagImagePath('sy'),
        label: 'SY +963',
        code: 'SY',
        dialCode: '+963',
    },
    {
        name: 'Taiwan',
        image: getFlagImagePath('tw'),
        label: 'TW +886',
        code: 'TW',
        dialCode: '+886',
    },
    {
        name: 'Tajikistan',
        image: getFlagImagePath('tj'),
        label: 'TJ +992',
        code: 'TJ',
        dialCode: '+992',
    },
    {
        name: 'Tanzania',
        image: getFlagImagePath('tz'),
        label: 'TZ +255',
        code: 'TZ',
        dialCode: '+255',
    },
    {
        name: 'Thailand',
        image: getFlagImagePath('th'),
        label: 'TH +66',
        code: 'TH',
        dialCode: '+66',
    },
    {
        name: 'Timor-Leste',
        image: getFlagImagePath('tl'),
        label: 'TL +670',
        code: 'TL',
        dialCode: '+670',
    },
    {
        name: 'Togo',
        image: getFlagImagePath('tg'),
        label: 'TG +228',
        code: 'TG',
        dialCode: '+228',
    },
    {
        name: 'Tokelau',
        image: getFlagImagePath('tk'),
        label: 'TK +690',
        code: 'TK',
        dialCode: '+690',
    },
    {
        name: 'Tonga',
        image: getFlagImagePath('to'),
        label: 'TO +676',
        code: 'TO',
        dialCode: '+676',
    },
    {
        name: 'Trinidad and Tobago',
        image: getFlagImagePath('tt'),
        label: 'TT +1868',
        code: 'TT',
        dialCode: '+1868',
    },
    {
        name: 'Tunisia',
        image: getFlagImagePath('tn'),
        label: 'TN +216',
        code: 'TN',
        dialCode: '+216',
    },
    {
        name: 'Turkey',
        image: getFlagImagePath('tr'),
        label: 'TR +90',
        code: 'TR',
        dialCode: '+90',
    },
    {
        name: 'Turkmenistan',
        image: getFlagImagePath('tm'),
        label: 'TM +993',
        code: 'TM',
        dialCode: '+993',
    },
    {
        name: 'Turks and Caicos Islands',
        image: getFlagImagePath('tc'),
        label: 'TC +1649',
        code: 'TC',
        dialCode: '+1649',
    },
    {
        name: 'Tuvalu',
        image: getFlagImagePath('tv'),
        label: 'TV +688',
        code: 'TV',
        dialCode: '+688',
    },
    {
        name: 'Uganda',
        image: getFlagImagePath('ug'),
        label: 'UG +256',
        code: 'UG',
        dialCode: '+256',
    },
    {
        name: 'Ukraine',
        image: getFlagImagePath('ua'),
        label: 'UA +380',
        code: 'UA',
        dialCode: '+380',
    },
    {
        name: 'United Arab Emirates',
        image: getFlagImagePath('ae'),
        label: 'AE +971',
        code: 'AE',
        dialCode: '+971',
    },
    {
        name: 'United Kingdom',
        image: getFlagImagePath('gb'),
        label: 'GB +44',
        code: 'GB',
        dialCode: '+44',
    },
    {
        name: 'United States',
        image: getFlagImagePath('us'),
        label: 'US +1',
        code: 'US',
        dialCode: '+1',
    },
    {
        name: 'Uruguay',
        image: getFlagImagePath('uy'),
        label: 'UY +598',
        code: 'UY',
        dialCode: '+598',
    },
    {
        name: 'Uzbekistan',
        image: getFlagImagePath('uz'),
        label: 'UZ +998',
        code: 'UZ',
        dialCode: '+998',
    },
    {
        name: 'Vanuatu',
        image: getFlagImagePath('vu'),
        label: 'VU +678',
        code: 'VU',
        dialCode: '+678',
    },
    {
        name: 'Venezuela',
        image: getFlagImagePath('ve'),
        label: 'VE +58',
        code: 'VE',
        dialCode: '+58',
    },
    {
        name: 'Vietnam',
        image: getFlagImagePath('vn'),
        label: 'VN +84',
        code: 'VN',
        dialCode: '+84',
    },
    {
        name: 'Virgin Islands, British',
        image: getFlagImagePath('vg'),
        label: 'VG +1284',
        code: 'VG',
        dialCode: '+1284',
    },
    {
        name: 'Virgin Islands, U.S.',
        image: getFlagImagePath('vi'),
        label: 'VI +1340',
        code: 'VI',
        dialCode: '+1340',
    },
    {
        name: 'Wallis and Futuna',
        image: getFlagImagePath('wf'),
        label: 'WF +681',
        code: 'WF',
        dialCode: '+681',
    },
    {
        name: 'Yemen',
        image: getFlagImagePath('ye'),
        label: 'YE +967',
        code: 'YE',
        dialCode: '+967',
    },
    {
        name: 'Zambia',
        image: getFlagImagePath('zm'),
        label: 'ZM +260',
        code: 'ZM',
        dialCode: '+260',
    },
    {
        name: 'Zimbabwe',
        image: getFlagImagePath('zw'),
        label: 'ZW +263',
        code: 'ZW',
        dialCode: '+263',
    },
];
